import { Box } from '@mui/material'
import { ReactNode } from 'react'
import StorageIcon from '@mui/icons-material/Storage'
import { FONT_MONO, FONT_SANS, TOKENS } from '../SchedulerDesign'

/**
 * Presentation pieces for the System of records page, in the Scheduler
 * language. The sidebar mirrors `SchedulerInput.sidebar.tsx` (its NAV tokens,
 * section header, eyebrow, rail and row states). That file exports only the
 * whole schedule sidebar — its pieces are private and tied to the schedule's
 * data status — so the parts SOR needs are mirrored here rather than shared.
 * Keep the two in step if the schedule sidebar's tokens move.
 */
const NAV = {
  teal900: '#0E3B4A',
  teal600: '#1F7A8C',
  bg: '#F3F5F6',
  hoverBg: '#EEF2F4',
  activeBg: '#E1EDF0',
  hairline: '#E2E7EA',
  rail: '#DCE3E6',
  text: '#2A3A44',
  textMuted: '#5B6A74',
  eyebrow: '#76848D',
  countBg: '#EBEFF1',
  tableRowH: 26,
  gutter: '12px',
  radius: 6,
  ease: '120ms ease',
} as const

const FOCUS_RING = {
  '&:focus-visible': {
    outline: `2px solid ${NAV.teal600}`,
    outlineOffset: '2px',
  },
} as const

const REDUCED_MOTION = {
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
} as const

export type SORNavItem = { id: string; label: string }

/**
 * Left navigation: a "Master data" header with the table count, then the
 * tables on a rail, exactly as the schedule page lists its data tables. There
 * is no status dot: the master tables carry no "edited since the last run"
 * state, and a dot that is always grey would read as "no data".
 */
export const SORSidebar = ({
  items,
  selected,
  onSelect,
}: {
  items: SORNavItem[]
  selected: string
  onSelect: (id: string) => void
}) => (
  <Box
    component="nav"
    aria-label="System of records"
    sx={{
      width: 230,
      minWidth: 230,
      flexShrink: 0,
      borderRight: `1px solid ${NAV.hairline}`,
      background: NAV.bg,
      overflowY: 'auto',
      pb: 2,
      fontFamily: FONT_SANS,
    }}
  >
    {/* Section header: same shape as the schedule sidebar's step headers,
        with the storage icon in the step badge's place (not collapsible —
        there is only the one section). */}
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mx: NAV.gutter,
        mt: 1.5,
        mb: 0.75,
        pb: '6px',
        borderBottom: `1px solid ${NAV.hairline}`,
      }}
    >
      <StorageIcon sx={{ fontSize: 15, color: NAV.teal900 }} />
      <Box
        component="span"
        sx={{
          flex: 1,
          fontSize: 10.5,
          fontWeight: 600,
          letterSpacing: '0.07em',
          textTransform: 'uppercase',
          color: NAV.teal900,
        }}
      >
        Master data
      </Box>
      <Box
        component="span"
        sx={{
          fontFamily: FONT_MONO,
          fontSize: 10,
          lineHeight: '15px',
          color: NAV.textMuted,
          background: NAV.countBg,
          borderRadius: '999px',
          px: '5px',
        }}
      >
        {items.length}
      </Box>
    </Box>

    {/* Eyebrow, as the schedule sidebar's "Data tables". */}
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: NAV.gutter,
        pt: 0.75,
        pb: 0.5,
      }}
    >
      <Box
        component="span"
        sx={{
          fontSize: 9.5,
          fontWeight: 600,
          letterSpacing: '0.07em',
          textTransform: 'uppercase',
          color: NAV.eyebrow,
        }}
      >
        Raw data tables
      </Box>
      <Box sx={{ flex: 1, height: '1px', background: NAV.hairline }} />
    </Box>

    <Box
      sx={{ mx: NAV.gutter, borderLeft: `2px solid ${NAV.rail}` }}
      role="list"
    >
      {items.map((item) => {
        const on = selected === item.id
        return (
          <Box
            key={item.id}
            component="button"
            type="button"
            role="listitem"
            onClick={() => onSelect(item.id)}
            aria-current={on ? 'page' : undefined}
            title={item.label}
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              height: NAV.tableRowH,
              pl: '10px',
              pr: 1,
              border: 'none',
              borderRadius: `${NAV.radius}px`,
              background: on ? NAV.activeBg : 'transparent',
              color: on ? NAV.teal900 : NAV.text,
              fontFamily: FONT_SANS,
              // Same reasoning as the schedule sidebar: 12px, 500 when active,
              // because Archivo is wide and heavier weights clip long names.
              fontSize: 12,
              fontWeight: on ? 500 : 400,
              textAlign: 'left',
              cursor: 'pointer',
              transition: `background-color ${NAV.ease}`,
              '&:hover': { background: on ? NAV.activeBg : NAV.hoverBg },
              ...FOCUS_RING,
              ...REDUCED_MOTION,
            }}
          >
            <Box
              component="span"
              sx={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item.label}
            </Box>
          </Box>
        )
      })}
    </Box>
  </Box>
)

/** A sub-table's title and note inside one tab (e.g. Tanks / Tank Eligibility). */
export const SORSubheading = ({
  title,
  children,
  first,
}: {
  title: string
  children?: ReactNode
  first?: boolean
}) => (
  <Box sx={{ mt: first ? 0 : 3, mb: 1 }}>
    <Box
      sx={{
        fontFamily: FONT_SANS,
        fontSize: 14,
        fontWeight: 700,
        color: TOKENS.ink900,
        mb: '2px',
      }}
    >
      {title}
    </Box>
    {children && (
      <Box
        sx={{
          fontSize: 12,
          lineHeight: 1.45,
          color: TOKENS.ink400,
          maxWidth: 860,
        }}
      >
        {children}
      </Box>
    )}
  </Box>
)

/**
 * Frames every grid in the SOR content area as a bordered white panel, like
 * the panels on Manage users. Targets BaseTable's own root class so the grids
 * rendered by shared `*.muiTable` components (synchronized lines, steriliser
 * routing, kitchen cycles) are framed too, without editing those files.
 */
export const SOR_TABLE_PANEL_SX = {
  '& .table-container-mui': {
    border: `1px solid ${TOKENS.line200}`,
    borderRadius: '2px',
    background: TOKENS.surface0,
  },
} as const
